import { DemandeValidationPj as DemandeValidationPjGql } from '@graphql/generated';
import { DemandeValidationPjStatutBackOfficeMapper } from './demande-validation-pj-statut-backoffice-mapper';
import { DemandeValidationPjStatutConsultantMapper } from './demande-validation-pj-statut-consultant-mapper';
import { DemandeValidationPj } from '@lib/models/demande-validation-pj/demande-validation-pj.model';
import { DemandeValidationPjTypeMapper } from './demande-validation-pj-type-mapper';
import { PieceJustificativeDocumentTypeMapper } from './piece-justificative-document-type-mapper';
import { DocumentModel } from '@lib/ged/document.model';
import { DocumentMapper } from './document-mapper';
import { PieceJustificativeKbis } from '@lib/models/demande-validation-pj/piece-justificative-kbis.model';
import { PieceJustificativeRsac } from '@lib/models/demande-validation-pj/piece-justificative-rsac.model';
import { DemandeValidationPjRcpMapper } from './demande-validation-pj-rcp-mapper';
import { PieceJustificativeAttVig } from '@lib/models/demande-validation-pj/piece-justificative-att-vig.model';
import { PieceJustificativeCerfa } from '@lib/models/demande-validation-pj/piece-justificative-cerfa.model';
import { PieceJustificativeNotifAff } from '@lib/models/demande-validation-pj/piece-justificative-notif-aff.model';
import { PieceJustificativeSyntUrssaf } from '@lib/models/demande-validation-pj/piece-justificative-synt-urssaf.model';
import { PieceJustificativeNotifDelaiUrssaf } from '@lib/models/demande-validation-pj/piece-justificative-notif-delai-urssaf.model';
import { PieceJustificativeContAgentCo } from '@lib/models/demande-validation-pj/piece-justificative-cont-agent-co.model';

export class DemandeValidationPjMapper {
  public static fromGraphQL(
    input: DemandeValidationPjGql,
    isBackOffice: boolean
  ): DemandeValidationPj {
    return {
      id: input.id,
      publicId: input.publicId,
      codeCom: input.codeCom!,
      type: DemandeValidationPjTypeMapper.fromGraphQl(input.demandeValidationPjType),
      dateUpdate: input.dateUpdate,
      dateDeclaration: input.dateDeclaration,
      statutConsultant: DemandeValidationPjStatutConsultantMapper.fromGraphQl(
        input.statutConsultant
      ),
      statutBackOffice: DemandeValidationPjStatutBackOfficeMapper.fromGraphQl(
        input.statutBackOffice
      ),
      gestionnaire: input.gestionnaire ?? undefined,
      gestionnaireId: input.gestionnaireId ?? undefined,
      nomUsage: input.nomUsage!,
      prenom: input.prenom!,
      documents: (input.demandeValidationPjDocuments ?? []).map(document => {
        if (document.document) {
          return DocumentMapper.fromGraphQl(document, isBackOffice)!;
        }

        return {
          id: document.id,
          declarationId: document.demandeValidationPjId,
          documentType: PieceJustificativeDocumentTypeMapper.fromGraphQl(
            document.documentTypeAsEnum
          ),
          dateUpdate: input.dateUpdate ? new Date(input.dateUpdate) : new Date(0),
          files: [],
          statut: 'a_corriger',
        } as DocumentModel;
      }),
      kbis: input?.kbis
        ? ({
            capitalSocial: input.kbis.capitalSocial,
            codePostal: input.kbis.codePostal,
            dateDebutValidite: input.kbis.dateDebutValidite,
            dateFinValidite: input.kbis.dateFinValidite,
            formeJuridique: input.kbis.formeJuridique,
            numeroSiret: input.kbis.numeroSiret,
            pays: input.kbis.pays,
            raisonSociale: input.kbis.raisonSociale,
            ville: input.kbis.ville,
            villeRegistreCommerce: input.kbis.villeRegistreCommerce,
            voie: input.kbis.voie,
            numeroSiren: input.kbis.numeroSiren,
            numeroTva: input.kbis.numeroTva,
          } as PieceJustificativeKbis)
        : undefined,
      rcp: input?.rcp ? DemandeValidationPjRcpMapper.fromGraphQL(input.rcp) : undefined,
      rsac: input?.rsac
        ? ({
            codePostal: input.rsac.codePostal,
            dateDebutValidite: input.rsac.dateDebutValidite,
            dateFinValidite: input.rsac.dateFinValidite,
            nomCommercial: input.rsac.nomCommercial,
            numeroSiret: input.rsac.numeroSiret,
            pays: input.rsac.pays,
            ville: input.rsac.ville,
            voie: input.rsac.voie,
          } as PieceJustificativeRsac)
        : undefined,
      attVig: input?.attVig
        ? ({
            dateDebutValidite: input.attVig.dateDebutValidite,
            dateFinValidite: input.attVig.dateFinValidite,
          } as PieceJustificativeAttVig)
        : undefined,
      cerfa: input?.cerfa
        ? ({
            dateDebutValidite: input.cerfa.dateDebutValidite,
            dateFinValidite: input.cerfa.dateFinValidite,
          } as PieceJustificativeCerfa)
        : undefined,
      notifAff: input?.notifAff
        ? ({
            dateDebutValidite: input.notifAff.dateDebutValidite,
            dateFinValidite: input.notifAff.dateFinValidite,
          } as PieceJustificativeNotifAff)
        : undefined,
      syntUrssaf: input?.syntUrssaf
        ? ({
            dateDebutValidite: input.syntUrssaf.dateDebutValidite,
            dateFinValidite: input.syntUrssaf.dateFinValidite,
          } as PieceJustificativeSyntUrssaf)
        : undefined,
      notifDelaiUrssaf: input?.notifDelaiUrssaf
        ? ({
            dateDebutValidite: input.notifDelaiUrssaf.dateDebutValidite,
            dateFinValidite: input.notifDelaiUrssaf.dateFinValidite,
          } as PieceJustificativeNotifDelaiUrssaf)
        : undefined,
      contAgentCo: input?.contAgentCo
        ? ({
            voie: input.contAgentCo.voie,
            codePostal: input.contAgentCo.codePostal,
            complementAdresse: input.contAgentCo.complementAdresse,
            ville: input.contAgentCo.ville,
            pays: input.contAgentCo.pays,
          } as PieceJustificativeContAgentCo)
        : undefined,
      refus: input.refus
        ? {
            id: input.refus.id,
            dateRefus: input.refus.dateRefus,
            gestionnare: input.refus.gestionnaireId,
            commentaire: input.refus.commentaire,
          }
        : undefined,
    };
  }
}
